import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import * as faceapi from '@vladmandic/face-api';
import { apiService } from '../services/api';
import './PhotoUpload.css';

const PhotoUpload = () => {
  const { userName } = useParams();
  const navigate = useNavigate();
  const webcamRef = useRef(null);

  const [actualUserId, setActualUserId] = useState(sessionStorage.getItem('userId') || null);
  const [userGender, setUserGender] = useState('');
  const [currentStep, setCurrentStep] = useState('old'); // 'old' or 'present'
  const [oldPhotos, setOldPhotos] = useState([]);
  const [presentPhotos, setPresentPhotos] = useState([]);
  const [postAPhotos, setPostAPhotos] = useState([]);
  const [postBPhotos, setPostBPhotos] = useState([]);
  const [presentUploadMethod, setPresentUploadMethod] = useState('files'); // 'files' or 'camera'
  const [postAUploadMethod, setPostAUploadMethod] = useState('files');
  const [postBUploadMethod, setPostBUploadMethod] = useState('files');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [examplePhotos, setExamplePhotos] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [displayName, setDisplayName] = useState(userName);
  const [duplicateDateWarning, setDuplicateDateWarning] = useState('');

  // Face Tracking State
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [isFacePerfect, setIsFacePerfect] = useState(false);
  const [isFaceDetected, setIsFaceDetected] = useState(false);
  const [targetAngleText, setTargetAngleText] = useState('STRAIGHT');
  const faceDetectionIntervalRef = useRef(null);

  // Refs for setInterval closures
  const currentStepRef = useRef(currentStep);
  const postAPhotosRef = useRef(postAPhotos);
  const postBPhotosRef = useRef(postBPhotos);

  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);
  useEffect(() => { postAPhotosRef.current = postAPhotos; }, [postAPhotos]);
  useEffect(() => { postBPhotosRef.current = postBPhotos; }, [postBPhotos]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e, uploadHandler) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fakeEvent = { target: { files: e.dataTransfer.files } };
      uploadHandler(fakeEvent);
    }
  };

  // Fetch existing user photos on load
  const loadUserPhotos = useCallback(async () => {
    if (!userName || userName === 'undefined') {
      navigate('/login');
      return;
    }
    
    // Auth Check
    const sessionUserName = sessionStorage.getItem('userName');
    const isAdminToken = sessionStorage.getItem('adminToken');
    if (!isAdminToken && sessionUserName && sessionUserName !== userName) {
      navigate(`/upload/${encodeURIComponent(sessionUserName)}`);
      return;
    }

    try {
      setLoading(true);
      const data = await apiService.getUserStatus(userName);
      if (data) {
        if (data.user?.id) setActualUserId(data.user.id);
        if (data.user?.name) setDisplayName(data.user.name);
        if (data.user?.gender) setUserGender(data.user.gender);
        if (data.old_photo_status?.photos) {
          setOldPhotos(data.old_photo_status.photos);
        }
        if (data.present_photos_status?.photos) {
          setPresentPhotos(data.present_photos_status.photos);
        }
        if (data.post_a_status?.photos) {
          setPostAPhotos(data.post_a_status.photos);
        }
        if (data.post_b_status?.photos) {
          setPostBPhotos(data.post_b_status.photos);
        }
      }
    } catch (err) {
      console.warn('Could not load existing photos:', err);
    } finally {
      setLoading(false);
    }
  }, [userName, navigate]);

  useEffect(() => {
    loadUserPhotos();
    apiService.getExamplePhotos().then(data => {
      setExamplePhotos(data.photos || []);
    }).catch(err => console.error('Failed to fetch example photos:', err));

    const loadFaceModels = async () => {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri('/models'),
        ]);
        setModelsLoaded(true);
      } catch (err) {
        console.error("Failed to load face detection models", err);
      }
    };
    loadFaceModels();
  }, [loadUserPhotos]);

  const validateFileSize = (file, isOld = true) => {
    const maxSize = 16 * 1024 * 1024; // 16MB
    const minSize = 128 * 1024; // 128KB for old photos

    if (file.size > maxSize) {
      setError(`"${file.name}" is too large. Maximum 16MB allowed.`);
      return false;
    }

    if (isOld && file.size < minSize) {
      setError(`"${file.name}" is too small (${(file.size / 1024).toFixed(0)}KB). Minimum 128KB required for old photos.`);
      return false;
    }

    return true;
  };

  // Upload old photos (multiple allowed)
  const handleOldPhotosUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    setDuplicateDateWarning(''); // Clear previous warning

    for (const file of files) {
      if (!validateFileSize(file, true)) continue;

      try {
        setLoading(true);
        const response = await apiService.uploadOldPhoto(actualUserId, [file]);
        if (response.photos) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setOldPhotos(prev => [...prev, ...photosWithPreview]);

          // Check if any uploaded photo was rejected due to duplicate date
          const allValidations = response.all_validations || (response.validation_results ? [response.validation_results] : []);
          for (const vr of allValidations) {
            const reasons = vr.rejection_reasons || [];
            const dupReason = reasons.find(r => r.toLowerCase().includes('duplicate date'));
            if (dupReason) {
              setDuplicateDateWarning(dupReason);
              break;
            }
          }
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    e.target.value = '';
  };

  // Upload present photos
  const uploadPresentPhotos = useCallback(async (files) => {
    for (const file of files) {
      try {
        setLoading(true);
        const response = await apiService.uploadPresentPhotos(actualUserId, [file]);
        if (response.photos && response.photos.length > 0) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setPresentPhotos(prev => [...prev, ...photosWithPreview]);
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [actualUserId, loadUserPhotos]);

  const handlePresentFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!validateFileSize(file, false)) {
        return;
      }
    }

    await uploadPresentPhotos(files);
    e.target.value = '';
  };

  const uploadPostAPhotos = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      try {
        setLoading(true);
        const response = await apiService.uploadPostAPhotos(actualUserId, [file]);
        if (response.photos && response.photos.length > 0) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setPostAPhotos(prev => [...prev, ...photosWithPreview]);
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [actualUserId, loadUserPhotos]);

  const handlePostAFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!validateFileSize(file, false)) {
        return;
      }
    }

    await uploadPostAPhotos(files);
    e.target.value = '';
  };

  const uploadPostBPhotos = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      try {
        setLoading(true);
        const response = await apiService.uploadPostBPhotos(actualUserId, [file]);
        if (response.photos && response.photos.length > 0) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setPostBPhotos(prev => [...prev, ...photosWithPreview]);
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [actualUserId, loadUserPhotos]);

  const handlePostBFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!validateFileSize(file, false)) {
        return;
      }
    }

    await uploadPostBPhotos(files);
    e.target.value = '';
  };

  const capturePhoto = useCallback(() => {
    if (!isFacePerfect) return; // Only capture if perfect angle
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      fetch(imageSrc)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `camera_${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          if (currentStep === 'present') {
            uploadPresentPhotos([file]);
          } else if (currentStep === 'post_a') {
            uploadPostAPhotos([file]);
          } else if (currentStep === 'post_b') {
            uploadPostBPhotos([file]);
          }
        })
        .catch(err => {
          console.error('Error converting image:', err);
          setError('Failed to capture photo. Please try again.');
        });
    }
  }, [uploadPresentPhotos, uploadPostAPhotos, uploadPostBPhotos, isFacePerfect, currentStep]);

  // Handle Video playing to start face detection
  const handleVideoPlay = () => {
    if (faceDetectionIntervalRef.current) {
      clearInterval(faceDetectionIntervalRef.current);
    }
    faceDetectionIntervalRef.current = setInterval(async () => {
      if (webcamRef.current && webcamRef.current.video && webcamRef.current.video.readyState === 4 && modelsLoaded) {
        const video = webcamRef.current.video;
        const detection = await faceapi.detectSingleFace(video, new faceapi.TinyFaceDetectorOptions()).withFaceLandmarks(true);

        if (detection) {
          setIsFaceDetected(true);
          const landmarks = detection.landmarks;

          // Calculate if the face is straight
          const nose = landmarks.getNose();
          const jawOutline = landmarks.getJawOutline();
          const leftEye = landmarks.getLeftEye();
          const rightEye = landmarks.getRightEye();

          // Horizontal alignment (yaw)
          const leftJawDist = nose[0].x - jawOutline[0].x;
          const rightJawDist = jawOutline[16].x - nose[0].x;
          const yawRatio = leftJawDist / rightJawDist;

          // Vertical alignment (pitch)
          const eyeY = (leftEye[3].y + rightEye[3].y) / 2;
          const noseY = nose[3].y; // bottom of nose
          const jawY = jawOutline[8].y; // chin

          const upperDist = noseY - eyeY;
          const lowerDist = jawY - noseY;
          const pitchRatio = upperDist / lowerDist;

          // Check if ratios are within a "perfect angle" tolerance
          const step = currentStepRef.current;
          let targetPitch = 'straight';
          let targetYaw = 'straight';
          let angleText = 'STRAIGHT';

          if (step === 'post_a') {
            const count = postAPhotosRef.current.length;
            if (count === 0) { targetPitch = 'up'; targetYaw = 'straight'; angleText = 'UP'; }
            else if (count === 1) { targetPitch = 'straight'; targetYaw = 'left'; angleText = 'LEFT'; }
            else if (count === 2) { targetPitch = 'down'; targetYaw = 'straight'; angleText = 'DOWN'; }
            else if (count === 3) { targetPitch = 'straight'; targetYaw = 'right'; angleText = 'RIGHT'; }
          } else if (step === 'post_b') {
            const count = postBPhotosRef.current.length;
            if (count === 0) { targetPitch = 'up'; targetYaw = 'left'; angleText = 'UP-LEFT'; }
            else if (count === 1) { targetPitch = 'up'; targetYaw = 'right'; angleText = 'UP-RIGHT'; }
            else if (count === 2) { targetPitch = 'down'; targetYaw = 'left'; angleText = 'DOWN-LEFT'; }
            else if (count === 3) { targetPitch = 'down'; targetYaw = 'right'; angleText = 'DOWN-RIGHT'; }
          }

          setTargetAngleText(angleText);

          let isYawPerfect = false;
          if (targetYaw === 'straight') {
            isYawPerfect = yawRatio > 0.8 && yawRatio < 1.25;
          } else if (targetYaw === 'left') {
            isYawPerfect = yawRatio < 0.65;
          } else if (targetYaw === 'right') {
            isYawPerfect = yawRatio > 1.5;
          }

          let isPitchPerfect = false;
          if (targetPitch === 'straight') {
            isPitchPerfect = pitchRatio > 0.6 && pitchRatio < 1.2;
          } else if (targetPitch === 'up') {
            isPitchPerfect = pitchRatio < 0.55;
          } else if (targetPitch === 'down') {
            isPitchPerfect = pitchRatio > 1.3;
          }

          if (isYawPerfect && isPitchPerfect) {
            setIsFacePerfect(true);
          } else {
            setIsFacePerfect(false);
          }
        } else {
          setIsFaceDetected(false);
          setIsFacePerfect(false);
        }
      }
    }, 150); // Run detection roughly 6-7 fps
  };

  useEffect(() => {
    if (!showCamera) {
      if (faceDetectionIntervalRef.current) {
        clearInterval(faceDetectionIntervalRef.current);
      }
      setIsFacePerfect(false);
      setIsFaceDetected(false);
    }
  }, [showCamera]);

  // Delete a specific photo
  const handleDeletePhoto = async (photoId, type) => {
    if (!window.confirm('Are you sure you want to remove this image?')) return;
    try {
      setLoading(true);
      await apiService.deletePhoto(photoId);
      if (type === 'old') {
        setOldPhotos(prev => prev.filter(p => p.id !== photoId));
      } else if (type === 'present') {
        setPresentPhotos(prev => prev.filter(p => p.id !== photoId));
      } else if (type === 'post_a') {
        setPostAPhotos(prev => prev.filter(p => p.id !== photoId));
      } else if (type === 'post_b') {
        setPostBPhotos(prev => prev.filter(p => p.id !== photoId));
      }
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to remove photo.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAllPhotos = async (type) => {
    let photosToRemove = [];
    if (type === 'old') photosToRemove = oldPhotos;
    else if (type === 'present') photosToRemove = presentPhotos;
    else if (type === 'post_a') photosToRemove = postAPhotos;
    else if (type === 'post_b') photosToRemove = postBPhotos;

    if (photosToRemove.length === 0) return;
    if (!window.confirm(`Are you sure you want to remove ALL ${photosToRemove.length} uploaded photos in this section?`)) return;

    try {
      setLoading(true);
      // Delete concurrently for speed
      await Promise.all(photosToRemove.map(p => apiService.deletePhoto(p.id)));

      if (type === 'old') setOldPhotos([]);
      else if (type === 'present') setPresentPhotos([]);
      else if (type === 'post_a') setPostAPhotos([]);
      else if (type === 'post_b') setPostBPhotos([]);

    } catch (err) {
      console.error('Error deleting all photos:', err);
      setError('Failed to remove all photos. Some may have been left behind.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleOverridePose = async (photoId, poseToAssign) => {
    if (!poseToAssign) return;
    try {
      setLoading(true);
      await apiService.overridePhotoPose(photoId, poseToAssign);

      // Update local state to reflect the new pose
      const updateFn = prev => prev.map(p => {
        if (p.id === photoId) {
          const newValidation = { ...(p.validation_results || {}) };
          newValidation.is_valid = true;
          newValidation.detected_pose = poseToAssign;
          return { ...p, is_valid: true, validation_results: newValidation };
        }
        return p;
      });

      setPostAPhotos(updateFn);
      setPostBPhotos(updateFn);
    } catch (err) {
      console.error('Error overriding pose:', err);
      setError('Failed to override photo pose.');
    } finally {
      setLoading(false);
    }
  };

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user"
  };

  const validOldPhotos = oldPhotos.filter(p => p.is_valid || p.validation_results?.is_valid);
  const hasPhotoOlderThanOneYear = validOldPhotos.some(p => {
    if (!p.validation_results?.exif_date) return false;
    const parts = p.validation_results.exif_date.split(' ')[0].split(':');
    const photoDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return photoDate < oneYearAgo;
  });
  const hasValidOldPhoto = validOldPhotos.length >= 16 && hasPhotoOlderThanOneYear;

  const validPresentPhotos = presentPhotos.filter(p => p.is_valid || p.validation_results?.is_valid);
  const isPresentComplete = validPresentPhotos.length >= 6;

  // Gate: Historical must have >= 16 total uploaded (accepted or not) to proceed
  const canAccessPresent = oldPhotos.length >= 16;
  // Gate: Present Neutral must have >= 6 total uploaded (accepted or not) to proceed
  const canAccessPost = canAccessPresent && presentPhotos.length >= 6;

  const goToDashboard = () => {
    navigate(`/dashboard/${encodeURIComponent(userName)}`);
  };

  return (
    <div className="photo-upload-container">
      <div className="upload-header-row" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem' }}>
        <img src="/logo192.png" alt="Imaginary Logo" style={{ height: '50px', width: 'auto', borderRadius: '12px' }} />
        <div className="upload-header">
          <h2>Imaginary — {displayName}</h2>
          <p className="subtitle" style={{ margin: 0 }}>Upload, inspect acceptance/rejection reasons, and submit your images</p>
        </div>
        <button onClick={goToDashboard} className="dashboard-shortcut-btn">
          View Dashboard
        </button>
      </div>

      <div className="collection-reason-card premium-glass">
        <div className="reason-decoration-circle"></div>
        <div className="reason-content" style={{ display: 'flex', flexDirection: 'row', gap: '2.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="reason-header" style={{ flex: '1', minWidth: '280px' }}>
            <div className="reason-badge">✨ AI Age Synthesis Project</div>
            <h4 style={{ margin: '0.5rem 0 0 0', color: '#1e293b', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1.2 }}>Why do we need these photos?</h4>
            <p style={{ color: '#64748b', marginTop: '0.75rem', fontSize: '0.95rem' }}>Your photos are essential for creating accurate, personalized models.</p>
          </div>
          <div className="reason-text-box" style={{ flex: '2', minWidth: '300px', margin: 0 }}>
            <p style={{ margin: '0 0 0.75rem 0' }}>
              <strong style={{ color: '#4f46e5', fontSize: '1.15rem' }}>Generative Face Aging & Digital Avatars</strong>
            </p>
            <p className="reason-description" style={{ margin: 0, color: '#475569', lineHeight: 1.6, fontSize: '0.95rem' }}>
              By providing your longitudinal photo history, you allow our generative models to learn your <em>authentic aging trajectories</em>. This enables us to create hyper-realistic, personalized digital avatars rather than relying on generic synthetic aging.
            </p>
          </div>
        </div>
      </div>

      {/* Premium Stepper UI */}
      <div className="premium-stepper-container">
        <div className={`stepper-step ${currentStep === 'old' ? 'active' : ''} ${hasValidOldPhoto ? 'completed' : ''}`} onClick={() => setCurrentStep('old')}>
          <div className="step-circle">{hasValidOldPhoto ? '✓' : '1'}</div>
          <div className="step-label">Historical Photos</div>
        </div>
        <div className={`stepper-line ${hasValidOldPhoto ? 'completed' : ''}`}></div>

        <div 
          className={`stepper-step ${currentStep === 'present' ? 'active' : ''} ${isPresentComplete ? 'completed' : ''} ${!canAccessPresent ? 'disabled' : ''}`} 
          onClick={() => { if (canAccessPresent) setCurrentStep('present'); }}
          style={{ cursor: canAccessPresent ? 'pointer' : 'not-allowed', opacity: canAccessPresent ? 1 : 0.5 }}
        >
          <div className="step-circle">{isPresentComplete ? '✓' : '2'}</div>
          <div className="step-label">Present Neutral</div>
        </div>

        {(!actualUserId || Number(actualUserId) % 2 !== 0) && (
          <>
            <div className={`stepper-line ${isPresentComplete ? 'completed' : ''}`}></div>
            <div 
              className={`stepper-step ${currentStep === 'post_a' ? 'active' : ''} ${!canAccessPost ? 'disabled' : ''}`} 
              onClick={() => { if (canAccessPost) setCurrentStep('post_a'); }}
              style={{ cursor: canAccessPost ? 'pointer' : 'not-allowed', opacity: canAccessPost ? 1 : 0.5 }}
            >
              <div className="step-circle">3</div>
              <div className="step-label">Variation A</div>
            </div>
          </>
        )}
        {(actualUserId && Number(actualUserId) % 2 === 0) && (
          <>
            <div className={`stepper-line ${isPresentComplete ? 'completed' : ''}`}></div>
            <div 
              className={`stepper-step ${currentStep === 'post_b' ? 'active' : ''} ${!canAccessPost ? 'disabled' : ''}`} 
              onClick={() => { if (canAccessPost) setCurrentStep('post_b'); }}
              style={{ cursor: canAccessPost ? 'pointer' : 'not-allowed', opacity: canAccessPost ? 1 : 0.5 }}
            >
              <div className="step-circle">3</div>
              <div className="step-label">Variation B</div>
            </div>
          </>
        )}
      </div>

      {/* STEP 1: OLD PHOTOS */}
      {currentStep === 'old' && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 1: Old Photos Review & Approval</h3>
            <p>Upload your past photo(s). Review automatic validation checks before approving.</p>
          </div>

          <div className="requirements">
            <p><strong>Validation Criteria:</strong></p>
            <ul>
              <li>Minimum file size: <strong>128 KB</strong></li>
              <li>Must contain a clearly recognizable human face</li>
              <li>Eyes must be visible and open</li>
              <li>You can upload multiple photos to select the best one</li>
              <li className="important-clause" style={{ color: '#d97706', fontWeight: 'bold' }}>
                Clause: Must Upload 50% must Upload from File Manager-&gt; DCIM-&gt; Camera ||
                You can also send us by through Email
              </li>
            </ul>

            <div className="example-images-section" style={{ marginTop: '1.5rem' }}>
              <p><strong>Examples of acceptable historical images:</strong></p>
              <div className="example-carousel-container" style={{ marginTop: '0.5rem', position: 'relative' }}>
                {examplePhotos.filter(p => {
                  const cat = (p.category || '').toLowerCase();
                  const target = ['female', 'girl', 'male'];
                  return target.includes(cat);
                }).length > 0 ? (
                  <>
                    <button onClick={(e) => e.currentTarget.parentElement.querySelector('.example-carousel').scrollBy({ left: -300, behavior: 'smooth' })} style={{ position: 'absolute', left: '-15px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '35px', height: '35px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.2rem' }}>&#8249;</button>
                    <div className="example-carousel">
                      {examplePhotos.filter(p => {
                        const cat = (p.category || '').toLowerCase();
                        const target = ['female', 'girl', 'male'];
                        return target.includes(cat);
                      }).map(photo => (
                        <div key={photo.id} className="example-carousel-item">
                          <img
                            src={`${process.env.REACT_APP_API_URL || `https://api.frinksyn.com/api`}/example-photos/${photo.id}/file`}
                            alt={`Historical sample for ${photo.category}`}
                            style={{ width: '100%', height: '300px', objectFit: 'contain', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#f8fafc' }}
                            onClick={() => setSelectedImage(`${process.env.REACT_APP_API_URL || `https://api.frinksyn.com/api`}/example-photos/${photo.id}/file`)}
                          />
                          <p style={{ textAlign: 'center', fontSize: '0.85rem', marginTop: '0.5rem', color: '#64748b', textTransform: 'capitalize' }}>For {photo.category}</p>
                        </div>
                      ))}
                    </div>
                    <button onClick={(e) => e.currentTarget.parentElement.querySelector('.example-carousel').scrollBy({ left: 300, behavior: 'smooth' })} style={{ position: 'absolute', right: '-15px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '35px', height: '35px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.2rem' }}>&#8250;</button>
                  </>
                ) : (
                  <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>No example images uploaded yet. Admin can upload them from the dashboard.</p>
                )}
              </div>
            </div>
          </div>

          <div className="file-upload">
            <label
              className={`file-upload-label ${isDragging ? 'drag-active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, handleOldPhotosUpload)}
            >
              <span className="upload-icon">📁</span>
              <span className="upload-text">Select Old Photo(s) from Device</span>
              <span className="upload-subtext">Click to browse or drag & drop (JPG, PNG)</span>
              <input
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleOldPhotosUpload}
                disabled={loading}
                className="file-input"
              />
            </label>
          </div>

          {/* Uploaded Old Photos Inspection Grid */}
          {duplicateDateWarning && (
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
              border: '2px solid #f59e0b',
              borderRadius: '8px',
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              boxShadow: '0 2px 8px rgba(245,158,11,0.25)'
            }}>
              <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.25rem', fontSize: '1rem' }}>
                  Duplicate Date Detected!
                </strong>
                <p style={{ color: '#78350f', margin: 0, fontSize: '0.9rem' }}>{duplicateDateWarning}</p>
                <p style={{ color: '#78350f', margin: '0.35rem 0 0', fontSize: '0.85rem' }}>
                  Each historical photo must have a unique capture date. Please upload a photo taken on a different date.
                </p>
              </div>
              <button
                onClick={() => setDuplicateDateWarning('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#92400e', lineHeight: 1, paddingTop: '0.1rem' }}
                title="Dismiss"
              >✕</button>
            </div>
          )}

          {oldPhotos.length > 0 && (
            <div className="photo-inspection-section">
              <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <h4>Uploaded Old Photos ({oldPhotos.length})</h4>
                  <span className="badge-summary">
                    {validOldPhotos.length} Accepted • {oldPhotos.length - validOldPhotos.length} Rejected
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveAllPhotos('old')}
                  style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Remove All
                </button>
              </div>

              <div className="cards-grid">
                {(() => {
                  // Detect duplicate EXIF dates across historical photos
                  const dateCounts = {};
                  oldPhotos.forEach(p => {
                    const d = (p.validation_results?.exif_date || '').split(' ')[0];
                    if (d) dateCounts[d] = (dateCounts[d] || 0) + 1;
                  });
                  const duplicateDates = new Set(Object.keys(dateCounts).filter(d => dateCounts[d] > 1));

                  return oldPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const isValid = photo.is_valid || val.is_valid;
                  const reasons = val.rejection_reasons || [];
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);

                  return (
                    <div key={photo.id || index} className={`photo-card ${isValid ? 'card-accepted' : 'card-rejected'}`}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Old upload ${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                          onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23334155'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='14'%3EPhoto Uploaded%3C/text%3E%3C/svg%3E"; }}
                        />
                        <div className={`status-pill ${isValid ? 'pill-success' : 'pill-danger'}`}>
                          {isValid ? '✅ Accepted' : '❌ Rejected'}
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Historical {index + 1}</h5>
                          <button
                            onClick={() => handleDeletePhoto(photo.id, 'old')}
                            className="delete-photo-btn"
                            title="Delete"
                          >
                            🗑️ Remove
                          </button>
                        </div>

                        {/* Checks list */}
                        <div className="checks-list">
                          <div className="check-row">
                            <span>File Size (&ge; 128KB):</span>
                            <span className={val.file_size_valid ? 'text-ok' : 'text-bad'}>
                              {val.file_size_valid ? '✓ Passed' : '✗ Too small'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Subjects Detected:</span>
                            <span className={val.subjects_detected > 0 ? 'text-ok' : 'text-bad'}>
                              {val.subjects_detected > 0 ? `✓ ${val.subjects_detected} found` : '✗ None'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Eyes Visible:</span>
                            <span className={val.eyes_visible ? 'text-ok' : 'text-bad'}>
                              {val.eyes_visible ? '✓ Visible' : '✗ Obscured'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Actual Date (EXIF):</span>
                            <span className={val.exif_date ? 'text-ok' : 'text-bad'}>
                              {val.exif_date ? <span>✓ {val.exif_date.split(' ')[0]}&nbsp;&nbsp;&nbsp;&nbsp;{val.exif_date.split(' ')[1] || ''}</span> : '✗ Not Found'}
                            </span>
                          </div>
                        </div>

                        {/* Rejection Reasons Box */}
                        {!isValid && (
                          <div className="rejection-box">
                            <span className="rejection-title">⚠️ Rejection Reason:</span>
                            {reasons.length > 0 ? (
                              <ul className="reasons-list">
                                {reasons.map((r, i) => <li key={i}>{r}</li>)}
                              </ul>
                            ) : (
                              <p className="single-reason">Does not meet the photo quality criteria.</p>
                            )}
                          </div>
                        )}

                        {/* Duplicate Date Warning */}
                        {(() => {
                          const dateOnly = (val.exif_date || '').split(' ')[0];
                          return dateOnly && duplicateDates.has(dateOnly) ? (
                            <div style={{
                              marginTop: '0.75rem',
                              padding: '0.65rem 0.85rem',
                              background: '#fff7ed',
                              border: '1.5px solid #f97316',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '0.5rem'
                            }}>
                              <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>⚠️</span>
                              <span style={{ fontSize: '0.82rem', color: '#9a3412', lineHeight: 1.45 }}>
                                <strong>Duplicate date detected.</strong> Another photo already has the same date (<strong>{dateOnly}</strong>). Each historical photo must be taken on a different day — please remove this one and upload a photo from a different date.
                              </span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  );
                  })
                })()}
              </div>

              {/* Pre-Approval Action Bar */}
              <div className="approval-action-bar">
                {canAccessPresent ? (
                  <div className="approval-box approved-state">
                    <div className="approval-text">
                      <strong>✅ 16 Photos Uploaded!</strong>
                      <p>You have uploaded {oldPhotos.length} historical photo(s) ({validOldPhotos.length} accepted). You may proceed to the next step.</p>
                    </div>
                    <button
                      onClick={() => setCurrentStep('present')}
                      className="approve-proceed-btn"
                    >
                      Next
                    </button>
                  </div>
                ) : (
                  <div className="approval-box rejected-state">
                    <div className="approval-text">
                      <strong>⚠️ {16 - oldPhotos.length} More Photo(s) Required</strong>
                      <p>You have uploaded {oldPhotos.length} of 16 required historical photos. Please upload at least 16 photos to proceed — not all need to be accepted.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="step-navigation">
            <div></div>
            <button className="step-nav-btn btn-next" onClick={() => setCurrentStep('present')} disabled={!canAccessPresent}
              title={!canAccessPresent ? `Upload ${16 - oldPhotos.length} more photo(s) to proceed` : 'Proceed to Present Neutral'}
            >
              Next Step &#8594;
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: PRESENT PHOTOS */}
      {currentStep === 'present' && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 2: Present Photos Verification (6 Required)</h3>
            <p>Upload or capture exactly 6 front-facing photos with distinctly different backgrounds.</p>
          </div>

          <div className="requirements">
            <p><strong>Present Photo Requirements:</strong></p>
            <ul>
              <li>Face must look straight at the camera (front-facing)</li>
              <li>Both shoulders must be clearly visible</li>
              <li>At least 50% of the photos (3 out of 6) must be without eyewear/glasses</li>
              <li>Each of the 6 photos must have a <strong>different background / location</strong></li>
              <li>Good lighting and clear focus</li>
            </ul>

            <div className="example-images-section" style={{ marginTop: '1.5rem' }}>
              <p><strong>Examples of acceptable present photos:</strong></p>
              <div className="example-carousel-container" style={{ marginTop: '0.5rem', position: 'relative' }}>
                {examplePhotos.filter(p => {
                  const cat = (p.category || '').toLowerCase();
                  const target = ['present_female', 'present_girl', 'present_male'];
                  return target.includes(cat);
                }).length > 0 ? (
                  <>
                    <button onClick={(e) => e.currentTarget.parentElement.querySelector('.example-carousel').scrollBy({ left: -300, behavior: 'smooth' })} style={{ position: 'absolute', left: '-15px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '35px', height: '35px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.2rem' }}>&#8249;</button>
                    <div className="example-carousel">
                      {examplePhotos.filter(p => {
                        const cat = (p.category || '').toLowerCase();
                        const target = ['present_female', 'present_girl', 'present_male'];
                        return target.includes(cat);
                      }).map(photo => (
                        <div key={photo.id} className="example-carousel-item">
                          <img
                            src={`${process.env.REACT_APP_API_URL || `https://api.frinksyn.com/api`}/example-photos/${photo.id}/file`}
                            alt={`Present sample for ${photo.category}`}
                            style={{ width: '100%', height: '300px', objectFit: 'contain', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#f8fafc' }}
                            onClick={() => setSelectedImage(`${process.env.REACT_APP_API_URL || `https://api.frinksyn.com/api`}/example-photos/${photo.id}/file`)}
                          />
                          <p style={{ textAlign: 'center', fontSize: '0.85rem', marginTop: '0.5rem', color: '#64748b', textTransform: 'capitalize' }}>For {photo.category.replace('present_', '')}</p>
                        </div>
                      ))}
                    </div>
                    <button onClick={(e) => e.currentTarget.parentElement.querySelector('.example-carousel').scrollBy({ left: 300, behavior: 'smooth' })} style={{ position: 'absolute', right: '-15px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '35px', height: '35px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.2rem' }}>&#8250;</button>
                  </>
                ) : (
                  <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.9rem' }}>No example images uploaded yet. Admin can upload them from the dashboard.</p>
                )}
              </div>
            </div>
          </div>

          {/* Upload Method Switcher */}
          <div className="method-selector">
            <button
              className={`method-btn ${presentUploadMethod === 'files' ? 'active' : ''}`}
              onClick={() => { setPresentUploadMethod('files'); setShowCamera(false); }}
            >
              📁 Multiple Files Upload
            </button>
            <button
              className={`method-btn ${presentUploadMethod === 'camera' ? 'active' : ''}`}
              onClick={() => { setPresentUploadMethod('camera'); }}
            >
              📷 Live Camera
            </button>
          </div>

          {/* Progress Bar */}
          <div className="photo-progress">
            <div className="progress-header">
              <span>Accepted Photos: <strong>{validPresentPhotos.length} / 6</strong></span>
              <span>Total Uploaded: {presentPhotos.length}</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${Math.min((validPresentPhotos.length / 6) * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Upload Dropzone */}
          {presentUploadMethod === 'files' && (
            <div className="file-upload">
              <label
                className={`file-upload-label ${isDragging ? 'drag-active' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, handlePresentFilesUpload)}
              >
                <span className="upload-icon">📂</span>
                <span className="upload-text">Upload Present Photos (Single or Multiple)</span>
                <span className="upload-subtext">Select files from your device</span>
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handlePresentFilesUpload}
                  disabled={loading}
                  className="file-input"
                />
              </label>
            </div>
          )}

          {/* Camera Section */}
          {presentUploadMethod === 'camera' && (
            <div className="camera-wrapper">
              {!showCamera && (
                <button onClick={() => setShowCamera(true)} className="start-camera-btn">
                  📷 Start Camera Session
                </button>
              )}

              {showCamera && (
                <div className="camera-modal-overlay">
                  <div className="camera-section">
                    <div className="camera-container">
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={videoConstraints}
                        className="webcam"
                        onPlay={handleVideoPlay}
                      />
                      <div className="camera-overlay">
                        <div className={`face-guide ${!isFaceDetected ? 'detecting' : isFacePerfect ? 'perfect' : 'imperfect'} angle-${targetAngleText.toLowerCase()}`}></div>
                      </div>
                    </div>
                    <div className="camera-controls">
                      <button
                        onClick={capturePhoto}
                        className="capture-btn"
                        disabled={loading || !isFacePerfect}
                        title={!isFacePerfect ? `Align your face for ${targetAngleText} first` : "Snap!"}
                      >
                        📸 Snap Photo ({validPresentPhotos.length + 1}/6)
                      </button>
                      <button onClick={() => setShowCamera(false)} className="close-camera-btn">
                        Close Camera
                      </button>
                    </div>
                    <p className="camera-instruction">
                      {modelsLoaded
                        ? (isFacePerfect ? "Perfect! Snap the photo now." : `Please look: ${targetAngleText}. Align until the outline turns green.`)
                        : "Loading face detection models..."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Present Photos Inspection Grid */}
          {presentPhotos.length > 0 && (
            <div className="photo-inspection-section">
              <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <h4>Uploaded Present Photos ({presentPhotos.length})</h4>
                  <span className="badge-summary">
                    {validPresentPhotos.length} Accepted • {presentPhotos.length - validPresentPhotos.length} Rejected
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveAllPhotos('present')}
                  style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Remove All
                </button>
              </div>

              <div className="cards-grid">
                {presentPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const isValid = photo.is_valid || val.is_valid;
                  const reasons = val.rejection_reasons || [];
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);

                  return (
                    <div key={photo.id || index} className={`photo-card ${isValid ? 'card-accepted' : 'card-rejected'}`}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Present upload ${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                          onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23334155'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='14'%3EPresent Photo%3C/text%3E%3C/svg%3E"; }}
                        />
                        <div className={`status-pill ${isValid ? 'pill-success' : 'pill-danger'}`}>
                          {isValid ? '✅ Accepted' : '❌ Rejected'}
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Photo #{index + 1}</h5>
                          <button
                            onClick={() => handleDeletePhoto(photo.id, 'present')}
                            className="delete-photo-btn"
                            title="Remove and replace image"
                          >
                            🗑️ Remove
                          </button>
                        </div>

                        {/* Checks list */}
                        <div className="checks-list">
                          <div className="check-row">
                            <span>Front Facing:</span>
                            <span className={val.front_facing !== false ? 'text-ok' : 'text-bad'}>
                              {val.front_facing !== false ? '✓ Direct' : '✗ Angled'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Shoulders Visible:</span>
                            <span className={val.shoulders_visible !== false ? 'text-ok' : 'text-bad'}>
                              {val.shoulders_visible !== false ? '✓ Visible' : '✗ Obscured'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Eyewear:</span>
                            <span style={{ fontWeight: 600, color: val.has_glasses ? '#d97706' : '#16a34a' }}>
                              {val.has_glasses ? '👓 Glasses detected' : '👁️ No glasses'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Actual Date (EXIF):</span>
                            <span className={val.exif_date ? 'text-ok' : 'text-bad'}>
                              {val.exif_date ? <span>✓ {val.exif_date.split(' ')[0]}&nbsp;&nbsp;&nbsp;&nbsp;{val.exif_date.split(' ')[1] || ''}</span> : '✗ Not Found'}
                            </span>
                          </div>
                        </div>

                        {/* Rejection Reasons */}
                        {!isValid && (
                          <div className="rejection-box">
                            <span className="rejection-title">⚠️ Rejection Reason:</span>
                            {reasons.length > 0 ? (
                              <ul className="reasons-list">
                                {reasons.map((r, i) => <li key={i}>{r}</li>)}
                              </ul>
                            ) : (
                              <p className="single-reason">Please replace with a sharp, front-facing photo.</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Final Pre-Approval and Submission */}
              <div className="approval-action-bar">
                {presentPhotos.length >= 6 ? (
                  <div className="approval-box approved-state">
                    <div className="approval-text">
                      <strong>✅ 6 Photos Uploaded!</strong>
                      <p>You have uploaded {presentPhotos.length} present photo(s) ({validPresentPhotos.length} accepted). You may proceed to the next step.</p>
                    </div>
                  </div>
                ) : (
                  <div className="approval-box warning-state">
                    <div className="approval-text">
                      <strong>⏳ {6 - presentPhotos.length} More Photo(s) Required</strong>
                      <p>You have uploaded {presentPhotos.length} of 6 required present photos. Upload at least 6 photos to proceed — not all need to be accepted.</p>
                    </div>
                  </div>
                )}
                
                <div className="step-navigation" style={{ width: '100%', marginTop: '1rem', borderTop: 'none', paddingTop: 0 }}>
                  <button className="step-nav-btn btn-prev" onClick={() => setCurrentStep('old')}>
                    &#8592; Previous
                  </button>
                  {(!actualUserId || Number(actualUserId) % 2 !== 0) ? (
                    <button className="step-nav-btn btn-next" onClick={() => setCurrentStep('post_a')} disabled={!canAccessPost}
                      title={!canAccessPost ? `Upload ${Math.max(0, 6 - presentPhotos.length)} more photo(s) to proceed` : 'Proceed to Variation A'}
                    >
                      Next Step &#8594;
                    </button>
                  ) : (
                    <button className="step-nav-btn btn-next" onClick={() => setCurrentStep('post_b')} disabled={!canAccessPost}
                      title={!canAccessPost ? `Upload ${Math.max(0, 6 - presentPhotos.length)} more photo(s) to proceed` : 'Proceed to Variation B'}
                    >
                      Next Step &#8594;
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing and validating image(s)...</p>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* STEP 3: POST Variation A */}
      {currentStep === 'post_a' && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 3: Post Variation A</h3>
            <p>Upload any additional requested Variation images here. This section is optional.</p>
          </div>

          <div className="requirements">
            <p><strong>Instructions — 4 Required Poses:</strong></p>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
              Take one photo per pose listed below. Look in the indicated direction while keeping your face fully in frame.
            </p>

            {/* Per-Pose Grid with embedded examples */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '1rem' }}>
              {[
                { key: 'up',    label: 'Pose 1 — UP',    arrow: '⬆️', desc: 'Tilt your chin upward, looking up toward the ceiling.' },
                { key: 'left',  label: 'Pose 2 — LEFT',  arrow: '⬅️', desc: 'Turn your head to the left side.' },
                { key: 'down',  label: 'Pose 3 — DOWN',  arrow: '⬇️', desc: 'Tilt your chin downward, looking toward the floor.' },
                { key: 'right', label: 'Pose 4 — RIGHT', arrow: '➡️', desc: 'Turn your head to the right side.' },
              ].map(pose => {
                const matchingPhoto = examplePhotos.find(p => {
                  const cat = (p.category || '').toLowerCase();
                  return cat.startsWith('posta') && cat.includes(pose.key);
                });
                return (
                  <div key={pose.key} style={{
                    background: '#ffffff',
                    border: '2px solid #e2e8f0',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    {/* Example photo */}
                    <div style={{ position: 'relative', background: '#f8fafc', minHeight: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {matchingPhoto ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${matchingPhoto.id}/file`}
                          alt={`Example for ${pose.label}`}
                          style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                          onClick={() => setSelectedImage(`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${matchingPhoto.id}/file`)}
                        />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem', color: '#94a3b8' }}>
                          <span style={{ fontSize: '2.5rem' }}>{pose.arrow}</span>
                          <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>No example uploaded</span>
                        </div>
                      )}
                      {/* Arrow badge overlay */}
                      <div style={{ position: 'absolute', top: '8px', left: '8px', background: 'rgba(15,23,42,0.75)', borderRadius: '8px', padding: '0.25rem 0.6rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span style={{ fontSize: '1rem' }}>{pose.arrow}</span>
                      </div>
                    </div>
                    {/* Pose info */}
                    <div style={{ padding: '0.85rem 1rem' }}>
                      <p style={{ margin: '0 0 0.35rem', fontWeight: '700', fontSize: '0.95rem', color: '#0f172a' }}>{pose.label}</p>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', lineHeight: 1.4, marginBottom: '0.85rem' }}>{pose.desc}</p>
                      
                      <label className="pose-upload-btn-friendly" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', textAlign: 'center', padding: '0.85rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 12px -2px rgba(59, 130, 246, 0.5)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.3)'; }}
                      >
                        <span style={{ fontSize: '1.2rem' }}>📸</span> Upload This Pose
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
                          const file = e.target.files[0];
                          if(file) {
                             const fakeEvent = { target: { files: [file] } };
                             handlePostAFilesUpload(fakeEvent);
                          }
                          e.target.value = '';
                        }} disabled={loading} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="method-selector">
            <button
              className="method-btn active"
              onClick={() => { setPostAUploadMethod('camera'); }}
              style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}
            >
              📷 Live Camera Session
            </button>
          </div>

          {postAUploadMethod === 'camera' && (
            <div className="camera-wrapper">
              {!showCamera && (
                <button onClick={() => setShowCamera(true)} className="start-camera-btn">
                  📷 Start Camera Session
                </button>
              )}

              {showCamera && (
                <div className="camera-modal-overlay">
                  <div className="camera-section">
                    <div className="camera-container">
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={videoConstraints}
                        className="webcam"
                        onPlay={handleVideoPlay}
                      />
                      <div className="camera-overlay">
                        <div className={`face-guide ${!isFaceDetected ? 'detecting' : isFacePerfect ? 'perfect' : 'imperfect'} angle-${targetAngleText.toLowerCase()}`}></div>
                      </div>
                    </div>
                    <div className="camera-controls">
                      <button
                        onClick={capturePhoto}
                        className="capture-btn"
                        disabled={loading || !isFacePerfect}
                        title={!isFacePerfect ? `Align your face for ${targetAngleText} first` : "Snap!"}
                      >
                        📸 Snap Photo ({postAPhotos.length + 1}/4)
                      </button>
                      <button onClick={() => setShowCamera(false)} className="close-camera-btn">
                        Close Camera
                      </button>
                    </div>
                    <p className="camera-instruction">
                      {modelsLoaded
                        ? (isFacePerfect ? "Perfect! Snap the photo now." : `Please look: ${targetAngleText}. Align until the outline turns green.`)
                        : "Loading face detection models..."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="photo-inspection-section" style={{ marginTop: '2rem' }}>
            <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4>Pose Conditions Analysis</h4>
              <button
                onClick={() => handleRemoveAllPhotos('post_a')}
                style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Remove All Variation A
              </button>
            </div>

            <div className="uploaded-photos-flat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
              {postAPhotos.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#94a3b8', fontStyle: 'italic', background: '#f8fafc', borderRadius: '8px' }}>
                  No Post A photos uploaded yet.
                </div>
              ) : (
                postAPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);
                  return (
                    <div key={photo.id || index} className="photo-card card-accepted" style={{ border: '2px solid #e2e8f0' }}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Variation upload ${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                        />
                      </div>
                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Image {index + 1}</h5>
                          <button
                            onClick={() => handleDeletePhoto(photo.id, 'post_a')}
                            className="delete-photo-btn"
                            title="Remove image"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                        <div className="checks-list">
                          <div className="check-row">
                            <span>Detected Pose:</span>
                            <span style={{ fontWeight: 'bold' }}>{val.detected_pose || 'UNASSIGNED'}</span>
                          </div>
                          <div className="check-row">
                            <span>Actual Date (EXIF):</span>
                            <span className={val.exif_date ? 'text-ok' : 'text-bad'}>
                              {val.exif_date ? <span>✓ {val.exif_date.split(' ')[0]}&nbsp;&nbsp;&nbsp;&nbsp;{val.exif_date.split(' ')[1] || ''}</span> : '✗ Not Found'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="step-navigation">
            <button className="step-nav-btn btn-prev" onClick={() => setCurrentStep('present')}>
              &#8592; Previous
            </button>
            <button className="step-nav-btn btn-next" onClick={goToDashboard}>
              Final Submit &#8594;
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: POST Variation B */}
      {currentStep === 'post_b' && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 4: Post Variation B</h3>
            <p>Upload any additional Variation B images here. This section is optional.</p>
          </div>

          <div className="requirements">
            <p><strong>Instructions — 4 Required Diagonal Poses:</strong></p>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
              Take one photo per diagonal pose below. Move your gaze in the combined direction indicated.
            </p>

            {/* Per-Pose Grid with embedded examples */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '1rem' }}>
              {[
                { key: 'up_left',    label: 'Pose 5 — UP-LEFT',    arrow: '↖️', desc: 'Look up and to the left diagonally.' },
                { key: 'up_right',   label: 'Pose 6 — UP-RIGHT',   arrow: '↗️', desc: 'Look up and to the right diagonally.' },
                { key: 'down_left',  label: 'Pose 7 — DOWN-LEFT',  arrow: '↙️', desc: 'Look down and to the left diagonally.' },
                { key: 'down_right', label: 'Pose 8 — DOWN-RIGHT', arrow: '↘️', desc: 'Look down and to the right diagonally.' },
              ].map(pose => {
                const matchingPhoto = examplePhotos.find(p => {
                  const cat = (p.category || '').toLowerCase();
                  return cat.startsWith('postb') && cat.includes(pose.key);
                });
                return (
                  <div key={pose.key} style={{
                    background: '#ffffff',
                    border: '2px solid #e2e8f0',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    {/* Example photo */}
                    <div style={{ position: 'relative', background: '#f8fafc', minHeight: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {matchingPhoto ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${matchingPhoto.id}/file`}
                          alt={`Example for ${pose.label}`}
                          style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                          onClick={() => setSelectedImage(`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${matchingPhoto.id}/file`)}
                        />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem', color: '#94a3b8' }}>
                          <span style={{ fontSize: '2.5rem' }}>{pose.arrow}</span>
                          <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>No example uploaded</span>
                        </div>
                      )}
                      {/* Arrow badge overlay */}
                      <div style={{ position: 'absolute', top: '8px', left: '8px', background: 'rgba(15,23,42,0.75)', borderRadius: '8px', padding: '0.25rem 0.6rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span style={{ fontSize: '1rem' }}>{pose.arrow}</span>
                      </div>
                    </div>
                    {/* Pose info */}
                    <div style={{ padding: '0.85rem 1rem' }}>
                      <p style={{ margin: '0 0 0.35rem', fontWeight: '700', fontSize: '0.95rem', color: '#0f172a' }}>{pose.label}</p>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', lineHeight: 1.4, marginBottom: '0.85rem' }}>{pose.desc}</p>
                      
                      <label className="pose-upload-btn-friendly" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', textAlign: 'center', padding: '0.85rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 12px -2px rgba(59, 130, 246, 0.5)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.3)'; }}
                      >
                        <span style={{ fontSize: '1.2rem' }}>📸</span> Upload This Pose
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
                          const file = e.target.files[0];
                          if(file) {
                             const fakeEvent = { target: { files: [file] } };
                             handlePostBFilesUpload(fakeEvent);
                          }
                          e.target.value = '';
                        }} disabled={loading} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="method-selector">
            <button
              className="method-btn active"
              onClick={() => { setPostBUploadMethod('camera'); }}
              style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}
            >
              📷 Live Camera Session
            </button>
          </div>

          {postBUploadMethod === 'camera' && (
            <div className="camera-wrapper">
              {!showCamera && (
                <button onClick={() => setShowCamera(true)} className="start-camera-btn">
                  📷 Start Camera Session
                </button>
              )}

              {showCamera && (
                <div className="camera-modal-overlay">
                  <div className="camera-section">
                    <div className="camera-container">
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={videoConstraints}
                        className="webcam"
                        onPlay={handleVideoPlay}
                      />
                      <div className="camera-overlay">
                        <div className={`face-guide ${!isFaceDetected ? 'detecting' : isFacePerfect ? 'perfect' : 'imperfect'} angle-${targetAngleText.toLowerCase()}`}></div>
                      </div>
                    </div>
                    <div className="camera-controls">
                      <button
                        onClick={capturePhoto}
                        className="capture-btn"
                        disabled={loading || !isFacePerfect}
                        title={!isFacePerfect ? `Align your face for ${targetAngleText} first` : "Snap!"}
                      >
                        📸 Snap Photo ({postBPhotos.length + 1}/4)
                      </button>
                      <button onClick={() => setShowCamera(false)} className="close-camera-btn">
                        Close Camera
                      </button>
                    </div>
                    <p className="camera-instruction">
                      {modelsLoaded
                        ? (isFacePerfect ? "Perfect! Snap the photo now." : `Please look: ${targetAngleText}. Align until the outline turns green.`)
                        : "Loading face detection models..."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="photo-inspection-section" style={{ marginTop: '2rem' }}>
            <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4>Pose Conditions Analysis</h4>
              <button
                onClick={() => handleRemoveAllPhotos('post_b')}
                style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Remove All Variation B
              </button>
            </div>

            <div className="uploaded-photos-flat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
              {postBPhotos.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#94a3b8', fontStyle: 'italic', background: '#f8fafc', borderRadius: '8px' }}>
                  No Post B photos uploaded yet.
                </div>
              ) : (
                postBPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);
                  return (
                    <div key={photo.id || index} className="photo-card card-accepted" style={{ border: '2px solid #e2e8f0' }}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Variation upload ${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                        />
                      </div>
                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Image {index + 1}</h5>
                          <button
                            onClick={() => handleDeletePhoto(photo.id, 'post_b')}
                            className="delete-photo-btn"
                            title="Remove image"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                        <div className="checks-list">
                          <div className="check-row">
                            <span>Detected Pose:</span>
                            <span style={{ fontWeight: 'bold' }}>{val.detected_pose || 'UNASSIGNED'}</span>
                          </div>
                          <div className="check-row">
                            <span>Actual Date (EXIF):</span>
                            <span className={val.exif_date ? 'text-ok' : 'text-bad'}>
                              {val.exif_date ? <span>✓ {val.exif_date.split(' ')[0]}&nbsp;&nbsp;&nbsp;&nbsp;{val.exif_date.split(' ')[1] || ''}</span> : '✗ Not Found'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="step-navigation">
            <button className="step-nav-btn btn-prev" onClick={() => setCurrentStep('present')}>
              &#8592; Previous
            </button>
            <button className="step-nav-btn btn-next" onClick={goToDashboard}>
              Final Submit &#8594;
            </button>
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="lightbox-overlay" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setSelectedImage(null)}>&times;</button>
            <img src={selectedImage} alt="Expanded view" />
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
